import type {
  ConversationDirection,
  ConversationStatus,
} from 'lib-shared-types-api';
import type { Channel } from 'lib-shared-types-channels';
import upperFirst from 'lodash/upperFirst';
import moment from 'moment-timezone';

import { numberFormatterInternational } from 'app/utils/telephonyHelpers';
import { channelAttributes } from 'utils/channels';
import { conversationStatusAttributes } from 'utils/conversation';
import { durationConverter } from 'utils/duration';

export type ConversationMetadataFields = {
  csid: number;
  channel?: Channel | null;
  direction?: ConversationDirection | null;
  status?: ConversationStatus | null;
  isAbandoned?: boolean | null;
  queueName?: string | null;
  createdAt?: string | null;
  closedAt?: string | null;
  attributes?: Partial<Record<string, unknown>> | null;
};

type ConversationMetadataLine = {
  key: string;
  value: string | undefined;
};

const getString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const formatDate = (date: string | null | undefined) =>
  date ? moment(date).format('LLL') : undefined;

const formatPhoneNumber = (value: unknown) => {
  const phoneNumber = getString(value);
  return phoneNumber && numberFormatterInternational({ phoneNumber });
};

const getStatus = (
  status: ConversationStatus | null | undefined,
  isAbandoned: boolean | null | undefined,
) => {
  if (!status) return undefined;

  const { label } = conversationStatusAttributes[status];
  const isFollowUp = status === 'pending' || status === 'awaitingpending';
  return !isFollowUp && isAbandoned ? `${label} (abandoned)` : label;
};

const getDuration = (
  createdAt: string | null | undefined,
  closedAt: string | null | undefined,
) => {
  if (!createdAt || !closedAt) return undefined;

  const { days, hours, minutes, seconds } = durationConverter.splitTimeUnits(
    moment(closedAt).diff(moment(createdAt), 'seconds'),
    true,
  );

  const time = [hours, minutes, seconds]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');

  if (days && days > 0) {
    return `${days} ${days === 1 ? 'day' : 'days'} ${time}`;
  }
  return time;
};

export const getConversationMetadataFields = ({
  csid,
  channel,
  direction,
  status,
  isAbandoned,
  queueName,
  createdAt,
  closedAt,
  attributes,
}: ConversationMetadataFields): ConversationMetadataLine[] => {
  const isClosed = status === 'closed';
  const isSms = channel === 'sms';

  const toEmail = getString(attributes?.to_email);
  const forwardedFromEmail = getString(attributes?.forwarded_from_email);

  const lines: ConversationMetadataLine[] = [
    { key: 'Conversation ID', value: `#${csid}` },
    {
      key: 'Channel',
      value: channel ? channelAttributes[channel]?.name : undefined,
    },
    { key: 'Direction', value: direction ? upperFirst(direction) : undefined },
    { key: 'Queue', value: queueName ?? undefined },
    { key: 'Status', value: getStatus(status, isAbandoned) },
    { key: 'Created at', value: formatDate(createdAt) },
    { key: 'Closed at', value: formatDate(closedAt) },
    { key: 'Subject', value: getString(attributes?.subject) },
    {
      key: 'Sent to',
      value:
        toEmail && direction === 'inbound' && forwardedFromEmail
          ? forwardedFromEmail
          : toEmail,
    },
    { key: 'Sent from', value: getString(attributes?.from_email) },
    {
      key: isSms ? 'Text to' : 'Call to',
      value: formatPhoneNumber(attributes?.called_number),
    },
    {
      key: isSms ? 'Texted from' : 'Calling from',
      value: formatPhoneNumber(attributes?.calling_number),
    },
    { key: 'Facebook page', value: getString(attributes?.facebook_page_name) },
    {
      key: 'Instagram account',
      value: getString(attributes?.instagram_account_username),
    },
    {
      key: 'Duration',
      value: isClosed ? getDuration(createdAt, closedAt) : undefined,
    },
  ];

  return lines.filter((line) => !!line.value);
};
